import React from 'react';
import { 
  Box, Container, Typography, Button, Paper, Divider, 
  List, ListItem, ListItemIcon, ListItemText, Alert, AlertTitle,
  Grid
} from '@mui/material';
import { 
  Gavel, CheckCircle, WarningAmber, Policy, 
  Copyright, Payment, Security, Cancel, 
  Public, Update, ContactSupport, Email
} from '@mui/icons-material';

export default function TermsAndConditions() {
  const primaryGradient = 'linear-gradient(135deg, #FF6A00 0%, #EE0979 100%)';
  const lightBg = '#f8fafc';

  // Helper component for list items
  const CustomListItem = ({ text }) => (
    <ListItem sx={{ py: 0.5, px: 0 }}>
      <ListItemIcon sx={{ minWidth: 32 }}>
        <CheckCircle sx={{ color: '#FF6A00', fontSize: 18 }} />
      </ListItemIcon>
      <ListItemText primary={text} primaryTypographyProps={{ color: '#475569', fontSize: '1rem' }} />
    </ListItem>
  );

  // Helper component for section headers
  const SectionHeader = ({ icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 5 }}>
      <Box 
        sx={{ 
          width: 40, height: 40, borderRadius: '10px', 
          background: 'rgba(255,106,0,0.1)', color: '#FF6A00',
          display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2
        }}
      >
        {icon}
      </Box>
      <Typography variant="h5" fontWeight="800" color="#1E293B">
        {title}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: lightBg, minHeight: '100vh', pb: 10 }}>
      
      {/* 🌟 PAGE HEADER 🌟 */}
      <Box sx={{ position: 'relative', pt: { xs: 12, md: 16 }, pb: { xs: 6, md: 8 }, bgcolor: '#070B14', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '60%', height: '2px', background: primaryGradient, boxShadow: '0 0 20px 5px rgba(255, 81, 47, 0.4)' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Gavel sx={{ fontSize: 50, color: '#FF6A00', mb: 2 }} />
          <Typography variant="h2" fontWeight="900" color="white" sx={{ mb: 2, letterSpacing: '-1px', fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            Terms & Conditions
          </Typography>
          <Typography variant="h6" color="#94A3B8" fontWeight="500">
            Last updated: January 2025
          </Typography>
        </Container>
      </Box>

      {/* 🌟 MAIN CONTENT 🌟 */}
      <Container maxWidth="md" sx={{ mt: -4, position: 'relative', zIndex: 2 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 3, md: 6 }, 
            borderRadius: 4, 
            border: '1px solid rgba(0,0,0,0.05)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.03)'
          }}
        >
          
          <Typography variant="body1" color="#475569" lineHeight={1.8} fontSize="1.1rem">
            Welcome to <strong>PracharApp</strong>. Please read these terms carefully before using our platform. 
            By accessing and using our service, you accept and agree to be bound by the terms and provisions of this agreement.
          </Typography>

          <SectionHeader icon={<Policy />} title="1. Description of Service" />
          <Typography variant="body1" color="#475569" mb={2} lineHeight={1.8}>
            PracharApp is a digital platform providing tools and templates for creating political posters, banners, and videos, designed specifically for political leaders and representatives in India. Our services include:
          </Typography>
          <List disablePadding>
            <CustomListItem text="Access to poster and video templates" />
            <CustomListItem text="Customization tools for text, images, and colors" />
            <CustomListItem text="High-resolution download capabilities" />
            <CustomListItem text="Multi-language support" />
            <CustomListItem text="Social media sharing features" />
          </List>

          {/* 🌟 ELECTION COMMISSION DISCLAIMER (HIGHLY HIGHLIGHTED) 🌟 */}
          <Box sx={{ mt: 6, mb: 4 }}>
            <Alert 
              icon={<WarningAmber fontSize="inherit" sx={{ color: '#d97706' }} />} 
              severity="warning"
              sx={{ 
                border: '1px solid #fcd34d', 
                bgcolor: '#fffbeb', 
                borderRadius: 3, 
                p: 3,
                '& .MuiAlert-icon': { fontSize: '2rem', mt: 0.5 }
              }}
            >
              <AlertTitle sx={{ fontWeight: '800', fontSize: '1.2rem', color: '#b45309', mb: 2 }}>
                चुनाव आयोग अस्वीकरण / Election Commission Disclaimer
              </AlertTitle>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight="800" color="#b45309" mb={1}>हिंदी में:</Typography>
                  <Typography variant="body2" color="#78350f" mb={1} lineHeight={1.6}>
                    <strong>PracharApp</strong> केवल एक डिज़ाइन प्लेटफॉर्म प्रदान करता है। <strong>भारतीय चुनाव आयोग के सभी नियमों और विनियमों का पालन करना पूर्णतः उम्मीदवार की जिम्मेदारी है।</strong>
                  </Typography>
                  <Typography variant="body2" color="#78350f" lineHeight={1.6}>
                    हम किसी भी उम्मीदवार का प्रचार नहीं करते। चुनावी नियमों, खर्च की सीमा, और प्रचार दिशानिर्देशों का पालन करना ग्राहक की जिम्मेदारी है।
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight="800" color="#b45309" mb={1}>In English:</Typography>
                  <Typography variant="body2" color="#78350f" mb={1} lineHeight={1.6}>
                    <strong>PracharApp</strong> solely provides a design platform. <strong>It is entirely the candidate's responsibility to follow all rules and regulations of the Election Commission of India.</strong>
                  </Typography>
                  <Typography variant="body2" color="#78350f" lineHeight={1.6}>
                    We do not promote any candidate. Following expenditure limits and campaign guidelines is solely the customer's responsibility.
                  </Typography>
                </Grid>
              </Grid>
            </Alert>
          </Box>

          <SectionHeader icon={<Security />} title="2. User Accounts" />
          <Typography variant="body1" color="#475569" mb={2} lineHeight={1.8}>
            To access certain features, you must create an account. You are responsible for:
          </Typography>
          <List disablePadding>
            <CustomListItem text="Maintaining the confidentiality of your credentials" />
            <CustomListItem text="All activities that occur under your account" />
            <CustomListItem text="Providing accurate and complete information" />
          </List>

          <SectionHeader icon={<Copyright />} title="3. Intellectual Property Rights" />
          <Typography variant="body1" color="#475569" mb={2} lineHeight={1.8}>
            The Service and its original content, features, and functionality are the exclusive property of PracharApp. However, regarding User Content:
          </Typography>
          <List disablePadding>
            <CustomListItem text="You retain ownership of content you create using our templates" />
            <CustomListItem text="You are responsible for ensuring you have rights to any uploaded images" />
          </List>

          <SectionHeader icon={<Payment />} title="4. Payment and Subscription" />
          <Typography variant="body1" color="#475569" mb={2} lineHeight={1.8}>
            Some features require payment. By purchasing a subscription, you agree to:
          </Typography>
          <List disablePadding>
            <CustomListItem text="Pay all fees associated with your chosen plan" />
            <CustomListItem text="Automatic renewal unless cancelled beforehand" />
            <CustomListItem text="Our refund policy as outlined on our website" />
          </List>

          <SectionHeader icon={<Cancel />} title="5. Termination" />
          <Typography variant="body1" color="#475569" lineHeight={1.8}>
            We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever, including without limitation if you breach these Terms.
          </Typography>

          <SectionHeader icon={<Public />} title="6. Governing Law" />
          <Typography variant="body1" color="#475569" lineHeight={1.8}>
            These Terms shall be interpreted and governed by the laws of India. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts in India.
          </Typography>

          <Divider sx={{ my: 6, borderColor: 'rgba(0,0,0,0.08)' }} />

          {/* 🌟 CONTACT SUPPORT SUB-SECTION 🌟 */}
          <Box sx={{ bgcolor: '#f1f5f9', p: 4, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight="800" color="#1E293B" mb={2} display="flex" alignItems="center">
              <ContactSupport sx={{ mr: 1, color: '#FF6A00' }} /> Have Questions?
            </Typography>
            <Typography variant="body2" color="#64748B" mb={3}>
              If you have any questions about these Terms and Conditions, please reach out to our legal and support team.
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="#1E293B" fontWeight="700">Email Us:</Typography>
                <Typography variant="body2" color="#475569">support@pracharapp.com</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="#1E293B" fontWeight="700">Call / WhatsApp:</Typography>
                <Typography variant="body2" color="#475569">+91 87651 40002</Typography>
              </Grid>
            </Grid>
          </Box>

        </Paper>
      </Container>

      {/* 🌟 BOTTOM CTA SECTION 🌟 */}
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Box 
          sx={{ 
            background: primaryGradient, borderRadius: 4, p: { xs: 4, md: 5 }, 
            textAlign: 'center', color: 'white', boxShadow: '0 10px 20px rgba(238, 9, 121, 0.2)'
          }}
        >
          <Typography variant="h4" fontWeight="900" mb={1}>Need Further Clarity?</Typography>
          <Typography variant="subtitle1" mb={3} sx={{ opacity: 0.9 }}>
            Our legal team is here to help clarify any questions you may have.
          </Typography>
          <Button 
            component="a" href="mailto:support@pracharapp.com"
            variant="contained" startIcon={<Email />}
            sx={{ 
              bgcolor: 'white', color: '#EE0979', px: 4, py: 1.5, 
              borderRadius: '50px', fontWeight: 'bold', fontSize: '1.05rem',
              '&:hover': { bgcolor: '#f8fafc', transform: 'translateY(-2px)' },
              transition: 'all 0.3s'
            }}
          >
            Contact Support Team
          </Button>
        </Box>
      </Container>

    </Box>
  );
}